import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnInit } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Channel } from '@ui';
import { addDays, addMonths, addWeeks, format, setDay, startOfMonth } from 'date-fns';
import { CalendarDay, CalendarFactory, CalendarMetadata, CalendarMonth, CalendarWeek } from '../calendar-factory';
import { CalendarRibbon } from './calendar-ribbon';
import { CalendarRibbonComponent } from './calendar-ribbon.component';

interface MockEvent {
  id: string;
  identifier: string;
  platform: Channel;
  startDate: Date;
  endDate: Date;
  blocked?: boolean;
}

interface RibbonData {
  reservation: MockEvent;
  blocked: boolean;
  startColumn: number;
  endColumn: number;
  startsThisWeek: boolean;
  endsThisWeek: boolean;
}

@Component({
  standalone: true,
  selector: 'ui-calendar-ribbon-example',
  templateUrl: './calendar-ribbon.example.component.html',
  imports: [CommonModule, MatTooltipModule, CalendarRibbonComponent],
})
export class CalendarRibbonExampleComponent implements OnInit, OnChanges {
  @Input() startOfWeek = 1; // 0 for Sunday, 1 for Monday
  @Input() numberOfMonths = 3;
  @Input() startDate: Date = new Date();

  calendarData: CalendarMetadata = CalendarFactory.createCalendar();
  dayLabels: string[] = [];

  platforms = [
    { name: Channel.Airbnb, label: 'Airbnb' },
    { name: Channel.Vrbo, label: 'VRBO' },
    { name: Channel.Booking, label: 'Booking.com' },
    { name: Channel.Direct, label: 'Direct' },
    { name: Channel.Agoda, label: 'Agoda' },
    { name: Channel.Manual, label: 'Manual' },
  ];

  // Mock reservations for demonstration
  mockReservations: MockEvent[] = this.generateMockReservations();

  ngOnInit(): void {
    this.generateCalendar();
  }

  ngOnChanges(): void {
    this.generateCalendar();
  }

  private generateCalendar(): void {
    this.calendarData = CalendarFactory.createCalendar({
      startOfWeek: this.startOfWeek,
      numberOfMonths: this.numberOfMonths,
      startDate: this.startDate,
    });

    this.dayLabels = CalendarFactory.getDayLabels(this.startOfWeek);
  }

  getWeekRibbons(week: CalendarWeek): RibbonData[] {
    const ribbons: RibbonData[] = [];
    const weekStart = week.days[0].date;
    const weekEnd = week.days[6].date;

    for (const reservation of this.mockReservations) {
      const resStart = new Date(reservation.startDate);
      const resEnd = new Date(reservation.endDate);

      // Check if reservation overlaps with this week
      if (resEnd >= weekStart && resStart <= weekEnd) {
        const startDay =
          resStart < weekStart
            ? week.days[0].dayOfWeek
            : (week.days.find((d: CalendarDay) => this.isSameDay(d.date, resStart))?.dayOfWeek ?? 0);

        const endDay =
          resEnd > weekEnd
            ? week.days[6].dayOfWeek
            : (week.days.find((d: CalendarDay) => this.isSameDay(d.date, resEnd))?.dayOfWeek ?? 6);

        const startsThisWeek = resStart >= weekStart && resStart <= weekEnd;
        const endsThisWeek = resEnd >= weekStart && resEnd <= weekEnd;

        const ribbon = new CalendarRibbon(startDay, endDay, startsThisWeek, endsThisWeek, this.startOfWeek);

        ribbons.push({
          reservation,
          blocked: reservation.blocked ?? false,
          startColumn: ribbon.startColumn,
          endColumn: ribbon.endColumn,
          startsThisWeek,
          endsThisWeek,
        });
      }
    }

    return ribbons;
  }

  private isSameDay(date1: Date, date2: Date): boolean {
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  }

  private generateMockReservations(): MockEvent[] {
    const today = new Date();
    const reservations: MockEvent[] = [];

    // Helper to get next occurrence of a specific day
    const getNextDay = (dayOfWeek: number, weeksAhead = 0): Date => {
      let date = startOfMonth(today);

      date = addWeeks(date, weeksAhead);
      date = setDay(date, dayOfWeek);

      return date;
    };

    // 1. Reservation that starts on Monday
    const mondayStart = getNextDay(1, 1); // Next Monday
    const mondayEnd = addDays(mondayStart, 3);
    reservations.push({
      id: '1',
      identifier: 'MON-START',
      platform: Channel.Airbnb,
      startDate: mondayStart,
      endDate: mondayEnd,
    });

    //  A blocked date
    reservations.push({
      id: '2',
      identifier: 'Blocked',
      blocked: true,
      platform: Channel.Manual,
      startDate: addDays(mondayEnd, 1),
      endDate: addDays(mondayEnd, 2),
    });

    // 2. Reservation that starts on Sunday
    const sundayStart = getNextDay(0, 2); // Next Sunday
    reservations.push({
      id: '2',
      identifier: 'SUN-START',
      platform: Channel.Vrbo,
      startDate: sundayStart,
      endDate: addDays(sundayStart, 2),
    });

    // 3. Reservation that spans multiple weeks (10 days)
    const multiWeekStart = addMonths(getNextDay(3, 2), 1); // Wednesday in 2 weeks
    reservations.push({
      id: '3',
      identifier: 'MULTI-WEEK',
      platform: Channel.Booking,
      startDate: multiWeekStart,
      endDate: addDays(multiWeekStart, 10),
    });

    // 5. Reservation that starts and ends in the same week
    const sameWeekStart = getNextDay(4, 2); // Tuesday in 2 weeks
    reservations.push({
      id: '5',
      identifier: 'SAME-WEEK',
      platform: Channel.Agoda,
      startDate: sameWeekStart,
      endDate: addDays(sameWeekStart, 2), // Ends Thursday same week
    });

    // 6. Back-to-back reservations (check-out/check-in same day)
    // First reservation ends on Saturday
    const backToBackStart1 = getNextDay(3, 4); // Wednesday in 4 weeks
    const backToBackEnd1 = addDays(backToBackStart1, 3); // Ends Saturday
    reservations.push({
      id: '6',
      identifier: 'CHECKOUT',
      platform: Channel.Manual,
      startDate: backToBackStart1,
      endDate: backToBackEnd1,
    });

    // Second reservation starts on Sunday (day after previous checkout)
    reservations.push({
      id: '7',
      identifier: 'CHECKIN',
      platform: Channel.Airbnb,
      startDate: addDays(backToBackEnd1, 1), // Starts on Sunday (day after Saturday checkout)
      endDate: addDays(backToBackEnd1, 4), // Ends Wednesday
    });

    return reservations;
  }

  trackByMonth(index: number, month: CalendarMonth): number {
    return month.monthIndex;
  }

  trackByWeek(index: number): number {
    return index;
  }

  trackByDay(index: number, day: CalendarDay): string {
    return day.dateString;
  }

  getDebugInfo(ribbon: RibbonData): string {
    return `Debug Info:
ID: ${ribbon.reservation.identifier}
Channel: ${ribbon.reservation.platform}
Start Date: ${format(ribbon.reservation.startDate, 'yyyy-MM-dd')}
End Date: ${format(ribbon.reservation.endDate, 'yyyy-MM-dd')}
Grid Columns: ${ribbon.startColumn} to ${ribbon.endColumn}
Starts This Week: ${ribbon.startsThisWeek}
Ends This Week: ${ribbon.endsThisWeek}`;
  }
}
